import React from 'react';
import { useField } from 'formik';
import { useDependentOptions } from './useDependentOptions';
import { UnifiedSelectVariant } from './variants/UnifiedSelectVariant';

export const DependentSelectField = ({ item = {}, formik = {} }) => {
  const {
    name,
    dependsOn = [],
    fetchConfig,
    fieldVariant = 'select',
    isMulti = false,
    isHierarchical = false, // eslint-disable-line no-unused-vars
    loadingText,
    emptyDependencyText,
    clearOnDependencyChange = true,
    enableLocalSearch = true,
    customOnChange = () => {},
    ...restProps
  } = item;

  const [_field, meta, helpers] = useField(name); // eslint-disable-line no-unused-vars
  const { setValue } = helpers;
  const { value: formikValue, error, touched } = meta;

  const { submitCount } = formik || {};
  const showError = (submitCount > 0 || touched) && error;

  // Custom hook for dependency management
  const {
    options,
    isLoading,
    hasAllDependencies,
    missingDependencies,
    error: fetchError,
  } = useDependentOptions({
    name,
    dependsOn,
    fetchConfig,
    formValues: formik.values,
    clearOnDependencyChange,
    onClear: setValue,
    isMulti,
  });

  // Enhanced onChange handler that calls both setValue and customOnChange
  const handleChange = (value) => {
    setValue(value);
    
    // Call customOnChange with the selected option object
    if (isMulti && Array.isArray(value)) {
      // For multi-select, find all selected options
      const selectedOptions = value.map(val => 
        options.find(option => option.value === val)
      ).filter(Boolean);
      customOnChange(selectedOptions);
    } else if (value) {
      // For single select, find the selected option
      const selectedOption = options.find(option => option.value === value);
      customOnChange(selectedOption);
    } else {
      // No selection
      customOnChange(null);
    }
  };

  // Generate better empty dependency text
  const generateEmptyDependencyText = () => {
    if (
      emptyDependencyText &&
      emptyDependencyText !== 'Please fill required fields first'
    ) {
      return emptyDependencyText;
    }

    if (missingDependencies.length === 0) {
      return 'Please fill required fields first';
    }

    // Use actual field labels from the dependency objects
    const missingFieldLabels = missingDependencies.map((dep) => dep.label);

    if (missingFieldLabels.length === 1) {
      return `Please fill "${missingFieldLabels[0]}" field first`;
    } else if (missingFieldLabels.length === 2) {
      return `Please fill "${missingFieldLabels[0]}" and "${missingFieldLabels[1]}" fields first`;
    } else {
      const lastField = missingFieldLabels.pop();
      return `Please fill "${missingFieldLabels.join(
        '", "'
      )}" and "${lastField}" fields first`;
    }
  };

  // Common props for all variants
  const commonProps = {
    options,
    isLoading,
    hasAllDependencies,
    value: formikValue,
    onChange: handleChange, // Use our enhanced handler
    loadingText,
    emptyDependencyText: generateEmptyDependencyText(),
    enableLocalSearch,
    error: fetchError,
    hasError: showError,
    ...restProps,
  };

  // Determine if this should be a multi-select
  const isMultiSelect = fieldVariant === 'multiSelect' || isMulti;

  // Render unified variant
  return <UnifiedSelectVariant {...commonProps} isMulti={isMultiSelect} />;
};
